import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { ApiError, apiFetch } from '../api/client.ts';
import { Button } from '../components/Button.tsx';
import { Card } from '../components/Card.tsx';
import { FormField } from '../components/FormField.tsx';
import { PortalFooter } from '../components/PortalFooter.tsx';
import { PortalNavBar } from '../components/PortalNavBar.tsx';
import { useDocumentTitle } from '../components/useDocumentTitle.ts';

interface PortalContact {
  id: string;
  organizationId: string;
  email: string;
  name: string | null;
  isRequester: boolean;
  isOrgAdmin: boolean;
  isOrgBilling: boolean;
}

interface TeamMember {
  id: string;
  email: string;
  name: string | null;
  isRequester: boolean;
  isOrgAdmin: boolean;
  isOrgBilling: boolean;
  isPortalUser: boolean;
  canViewAllOrgTickets: boolean;
  subscribeCcAllTickets: boolean;
  totpEnrolledAt: string | null;
  lastLoginAt: string | null;
}

export function PortalTeamPage(): React.ReactElement {
  useDocumentTitle('Team');
  const navigate = useNavigate();
  const [me, setMe] = useState<PortalContact | null>(null);
  const [members, setMembers] = useState<TeamMember[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [adding, setAdding] = useState(false);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [isRequester, setIsRequester] = useState(true);
  const [isOrgAdmin, setIsOrgAdmin] = useState(false);
  const [isOrgBilling, setIsOrgBilling] = useState(false);
  const [sendInvite, setSendInvite] = useState(true);
  const [busy, setBusy] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [inviteResult, setInviteResult] = useState<string | null>(null);
  const [actionBusy, setActionBusy] = useState<string | null>(null);

  async function loadMembers() {
    try {
      const r = await apiFetch<{ contacts: TeamMember[] }>('/api/portal/contacts');
      setMembers(r.contacts);
      setLoadError(null);
    } catch (err) {
      setLoadError(err instanceof ApiError ? err.message : 'Failed to load team.');
    }
  }

  useEffect(() => {
    apiFetch<{ contact: PortalContact | null }>('/api/portal/auth/me')
      .then(async (r) => {
        if (!r.contact) {
          navigate('/login', { replace: true, state: { from: '/portal/team' } });
          return;
        }
        if (!r.contact.isOrgAdmin) {
          setLoadError('Only org admins can manage the team.');
          setMe(r.contact);
          return;
        }
        setMe(r.contact);
        await loadMembers();
      })
      .catch((err: unknown) => {
        if (err instanceof ApiError && (err.status === 401 || err.status === 403)) {
          navigate('/login', { replace: true, state: { from: '/portal/team' } });
          return;
        }
        setLoadError(err instanceof ApiError ? err.message : 'Failed to load.');
      });
  }, [navigate]);

  async function addMember(e: React.FormEvent) {
    e.preventDefault();
    setCreateError(null);
    setInviteResult(null);
    setBusy(true);
    try {
      const r = await apiFetch<{
        contact: TeamMember;
        enrollmentUrl: string | null;
        emailSent: boolean;
        emailError: string | null;
        expiresInHours: number | null;
      }>('/api/portal/contacts', {
        method: 'POST',
        json: {
          email: email.trim(),
          name: name.trim() || null,
          isRequester,
          isOrgAdmin,
          isOrgBilling,
          sendInvite,
        },
      });
      setEmail('');
      setName('');
      setIsRequester(true);
      setIsOrgAdmin(false);
      setIsOrgBilling(false);
      setSendInvite(true);
      setAdding(false);
      if (r.enrollmentUrl) {
        const lines = [
          `Invite for ${r.contact.email}:`,
          r.enrollmentUrl,
          `(Expires in ${r.expiresInHours}h.)`,
        ];
        if (r.emailSent) lines.push('Email also sent.');
        else if (r.emailError) lines.push(`Email not sent: ${r.emailError}`);
        setInviteResult(lines.join('\n'));
      }
      await loadMembers();
    } catch (err) {
      setCreateError(err instanceof ApiError ? err.message : 'Failed to add team member.');
    } finally {
      setBusy(false);
    }
  }

  async function patchMember(id: string, patch: Partial<TeamMember>) {
    setActionBusy(id);
    try {
      await apiFetch(`/api/portal/contacts/${id}`, { method: 'PATCH', json: patch });
      await loadMembers();
    } catch (err) {
      setLoadError(err instanceof ApiError ? err.message : 'Failed to update.');
    } finally {
      setActionBusy(null);
    }
  }

  async function reInvite(id: string, contactEmail: string) {
    setActionBusy(id);
    setInviteResult(null);
    try {
      const r = await apiFetch<{
        enrollmentUrl: string;
        emailSent: boolean;
        emailError: string | null;
        expiresInHours: number;
      }>(`/api/portal/contacts/${id}/invite`, { method: 'POST' });
      const lines = [
        `Invite for ${contactEmail}:`,
        r.enrollmentUrl,
        `(Expires in ${r.expiresInHours}h.)`,
      ];
      if (r.emailSent) lines.push('Email also sent.');
      else if (r.emailError) lines.push(`Email not sent: ${r.emailError}`);
      setInviteResult(lines.join('\n'));
      await loadMembers();
    } catch (err) {
      setInviteResult(err instanceof ApiError ? err.message : 'Failed to send invite.');
    } finally {
      setActionBusy(null);
    }
  }

  async function removeMember(id: string, contactEmail: string) {
    if (!window.confirm(`Remove ${contactEmail} from your organization?`)) return;
    setActionBusy(id);
    try {
      await apiFetch(`/api/portal/contacts/${id}`, { method: 'DELETE' });
      await loadMembers();
    } catch (err) {
      setLoadError(err instanceof ApiError ? err.message : 'Failed to remove.');
    } finally {
      setActionBusy(null);
    }
  }

  return (
    <div className="bg-white">
      <PortalNavBar contact={me} active="team" />

      <div className="mx-auto max-w-7xl px-6 py-12 sm:px-8">
        <div className="flex items-end justify-between gap-3">
          <div>
            <h1 className="font-display text-4xl font-extrabold text-[var(--color-header)]">Team</h1>
            <p className="mt-2 text-sm text-[var(--color-muted)]">
              Manage everyone in your organization who can submit tickets, see billing, or
              administer the team.
            </p>
          </div>
          {me?.isOrgAdmin && (
            <Button onClick={() => { setAdding((v) => !v); setInviteResult(null); }}>
              {adding ? 'Cancel' : '+ Add team member'}
            </Button>
          )}
        </div>

        {loadError && (
          <Card className="mt-6 border-[var(--color-danger)]/30 bg-red-50/40">
            <p className="text-sm text-[var(--color-danger)]">{loadError}</p>
          </Card>
        )}

        {adding && (
          <Card className="mt-6">
            <form onSubmit={addMember} className="grid gap-4 sm:grid-cols-2">
              <FormField
                label="Email"
                name="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              <FormField label="Name" name="name" value={name} onChange={(e) => setName(e.target.value)} />
              <div className="sm:col-span-2">
                <span className="mb-2 block text-sm font-medium text-[var(--color-header)]">Roles</span>
                <div className="flex flex-wrap gap-4 text-sm">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={isRequester}
                      onChange={(e) => setIsRequester(e.target.checked)}
                      className="h-4 w-4 accent-[var(--color-t3-blue)]"
                    />
                    Requester
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={isOrgAdmin}
                      onChange={(e) => setIsOrgAdmin(e.target.checked)}
                      className="h-4 w-4 accent-[var(--color-t3-blue)]"
                    />
                    Org admin
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={isOrgBilling}
                      onChange={(e) => setIsOrgBilling(e.target.checked)}
                      className="h-4 w-4 accent-[var(--color-t3-blue)]"
                    />
                    Org billing
                  </label>
                </div>
              </div>
              <label className="flex items-center gap-2 text-sm sm:col-span-2">
                <input
                  type="checkbox"
                  checked={sendInvite}
                  onChange={(e) => setSendInvite(e.target.checked)}
                  className="h-4 w-4 accent-[var(--color-t3-blue)]"
                />
                Send portal enrollment email (so they can set a password and 2FA)
              </label>
              {createError && <p className="text-sm text-[var(--color-danger)] sm:col-span-2">{createError}</p>}
              <div className="sm:col-span-2">
                <Button type="submit" disabled={busy || !email.trim()}>
                  {busy ? 'Adding…' : 'Add team member'}
                </Button>
              </div>
            </form>
          </Card>
        )}

        {inviteResult && (
          <Card className="mt-6 border-[var(--color-t3-blue)]/30 bg-blue-50/40">
            <pre className="whitespace-pre-wrap break-all text-xs text-[var(--color-body)]">{inviteResult}</pre>
          </Card>
        )}

        <div className="mt-8 overflow-hidden rounded-2xl border border-[var(--color-border)] bg-white">
          {!members ? (
            <p className="p-6 text-sm text-[var(--color-muted)]">Loading…</p>
          ) : members.length === 0 ? (
            <p className="p-6 text-center text-sm text-[var(--color-muted)]">
              No team members yet. Add one above.
            </p>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-[var(--color-surface)] text-left text-xs uppercase tracking-wide text-[var(--color-muted)]">
                <tr>
                  <th className="px-4 py-3">Email</th>
                  <th className="px-4 py-3">Name</th>
                  <th className="px-4 py-3">Roles</th>
                  <th className="px-4 py-3 text-center" title="Sees every ticket for the org, not just their own">View all</th>
                  <th className="px-4 py-3 text-center" title="CC'd on every outbound ticket reply for the org">CC all</th>
                  <th className="px-4 py-3">Portal</th>
                  <th className="px-4 py-3 text-right"></th>
                </tr>
              </thead>
              <tbody>
                {members.map((m) => (
                  <tr key={m.id} className="border-t border-[var(--color-border)]">
                    <td className="px-4 py-3 font-mono text-xs">
                      {m.email} {me?.id === m.id && <span className="text-[var(--color-muted)]">(you)</span>}
                    </td>
                    <td className="px-4 py-3">{m.name ?? '—'}</td>
                    <td className="px-4 py-3">
                      <div className="flex flex-col gap-1">
                        <RoleCheckbox
                          checked={m.isRequester}
                          disabled={actionBusy === m.id}
                          onChange={(v) => void patchMember(m.id, { isRequester: v })}
                          label="Requester"
                        />
                        <RoleCheckbox
                          checked={m.isOrgAdmin}
                          disabled={actionBusy === m.id || m.id === me?.id}
                          onChange={(v) => void patchMember(m.id, { isOrgAdmin: v })}
                          label="Org admin"
                        />
                        <RoleCheckbox
                          checked={m.isOrgBilling}
                          disabled={actionBusy === m.id}
                          onChange={(v) => void patchMember(m.id, { isOrgBilling: v })}
                          label="Org billing"
                        />
                      </div>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <input
                        type="checkbox"
                        checked={m.canViewAllOrgTickets}
                        disabled={actionBusy === m.id}
                        onChange={(e) => void patchMember(m.id, { canViewAllOrgTickets: e.target.checked })}
                        className="h-4 w-4 accent-[var(--color-t3-blue)]"
                      />
                    </td>
                    <td className="px-4 py-3 text-center">
                      <input
                        type="checkbox"
                        checked={m.subscribeCcAllTickets}
                        disabled={actionBusy === m.id}
                        onChange={(e) => void patchMember(m.id, { subscribeCcAllTickets: e.target.checked })}
                        className="h-4 w-4 accent-[var(--color-t3-blue)]"
                      />
                    </td>
                    <td className="px-4 py-3 text-xs">
                      {m.totpEnrolledAt ? (
                        <span className="text-green-700">enrolled</span>
                      ) : m.isPortalUser ? (
                        <span className="text-amber-700">invited</span>
                      ) : (
                        <span className="text-[var(--color-muted)]">none</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex justify-end gap-2">
                        {!m.totpEnrolledAt && (
                          <Button
                            variant="outline"
                            onClick={() => void reInvite(m.id, m.email)}
                            disabled={actionBusy === m.id}
                          >
                            {m.isPortalUser ? 'Re-invite' : 'Invite'}
                          </Button>
                        )}
                        {m.id !== me?.id && (
                          <Button
                            variant="outline"
                            onClick={() => void removeMember(m.id, m.email)}
                            disabled={actionBusy === m.id}
                          >
                            Remove
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <p className="mt-4 text-xs text-[var(--color-muted)]">
          ← <Link to="/portal" className="hover:underline">Back to help center</Link>
        </p>
      </div>

      <PortalFooter />
    </div>
  );
}

function RoleCheckbox({
  checked,
  disabled,
  onChange,
  label,
}: {
  checked: boolean;
  disabled: boolean;
  onChange: (v: boolean) => void;
  label: string;
}): React.ReactElement {
  return (
    <label className="flex items-center gap-1 text-xs whitespace-nowrap">
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        className="h-3.5 w-3.5 accent-[var(--color-t3-blue)]"
      />
      {label}
    </label>
  );
}
